// packages/components/src/admin/GoalEditorModal.tsx

import React, { useEffect, useRef } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { Modal, TextField, Button, Spinner, CheckboxField } from '@dopomogai/ui';
import type { Goal } from '@dopomogai/types';

// Simple deep object comparison function to avoid external dependencies.
const isObjectEqual = (objA: any, objB: any): boolean => {
    if (objA === objB) return true;
    if (objA == null || objB == null || typeof objA !== 'object' || typeof objB !== 'object') {
        return false;
    }
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
        if (!keysB.includes(key) || !isObjectEqual(objA[key], objB[key])) {
            return false;
        }
    }
    return true;
};

interface GoalEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Goal) => Promise<void>;
  isSaving: boolean;
  goalData: Partial<Goal> | null;
  onDataChange: (data: Partial<Goal>) => void;
}

export const GoalEditorModal: React.FC<GoalEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  goalData,
  onDataChange,
}) => {
  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<Goal>({
    // --- CHANGE: Added target_value to defaultValues ---
    defaultValues: {
      _id: '',
      name: '',
      description: '',
      is_manual: true,
      target_value: 0,
    }
  });

  const lastSyncedData = useRef<Partial<Goal> | null>(null);

  useEffect(() => {
    if (isOpen && goalData) {
      if (!isObjectEqual(goalData, lastSyncedData.current)) {
        reset(goalData);
        lastSyncedData.current = goalData;
      }
    }
  }, [isOpen, goalData, reset]);

  const onDataChangeRef = useRef(onDataChange);
  useEffect(() => { onDataChangeRef.current = onDataChange; });

  useEffect(() => {
    const subscription = watch((value) => {
      if (goalData && !isObjectEqual(value, goalData)) {
        onDataChangeRef.current(value as Partial<Goal>);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, goalData]);

  const onSubmit: SubmitHandler<Goal> = (data) => {
    // --- CHANGE: Ensure target_value is a number before saving ---
    const dataToSave = {
      ...data,
      target_value: Number(data.target_value) || 0,
    };
    onSave(dataToSave);
  };

  const formTitle = goalData?._id ? `Edit Goal: ${goalData.name}` : 'Create New Goal Definition';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={formTitle}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Controller
          name="_id"
          control={control}
          rules={{ 
            required: 'ID is required',
            pattern: { value: /^[a-z0-9-]+$/, message: 'ID must be lowercase, numbers, and hyphens only' }
          }}
          render={({ field }) => (
            <TextField 
              label="Goal ID (Unique Identifier)"
              {...field}
              disabled={!!goalData?._id}
              errorText={errors._id?.message}
              placeholder="A unique key for this goal, e.g., 'beta-users-total'. Cannot be changed."
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field }) => <TextField label="Display Name" {...field} errorText={errors.name?.message} />}
        />
        <Controller
          name="description"
          control={control}
          rules={{ required: 'Description is required' }}
          render={({ field }) => (
            <TextField label="Description" {...field} errorText={errors.description?.message} />
          )}
        />
        
        {/* --- NEW FIELD FOR TARGET VALUE --- */}
        <Controller
          name="target_value"
          control={control}
          render={({ field }) => (
            <TextField 
              label="Default Target Value (Optional)"
              type="number"
              {...field}
              onChange={e => field.onChange(parseFloat(e.target.value) || 0)} // Ensure value is number
              placeholder="Set a default target for this KPI. Can be overridden in specific sprint objectives."
            />
          )}
        />
        
        <Controller
          name="is_manual"
          control={control}
          render={({ field }) => (
            <CheckboxField
              label="Track this goal manually?"
              checked={!!field.value}
              onChange={field.onChange}
              description="Check this if an admin needs to enter the value. Uncheck for automatic tracking."
            />
          )}
        />
        <div className="flex justify-end gap-3 pt-4">
          <Button text="Cancel" variant="outline" onClick={onClose} disabled={isSaving} />
          <Button
            text={isSaving ? "Saving..." : "Save Goal"}
            type="submit"
            disabled={isSaving}
            icon={isSaving ? <Spinner size="sm" /> : "Save"}
          />
        </div>
      </form>
    </Modal>
  );
};